import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Bell,
  CalendarDays,
  MapPin,
  Clock3,
  Truck,
  Package,
  Route,
  Flag,
  ArrowRight,
  Circle,
  CheckCircle2,
  AlertCircle,
  MessageSquareWarning,
  FileWarning,
  FileText,
  Megaphone,
  ChevronRight
} from 'lucide-react';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';

const STATUS_CONFIG = {
  prevu: { label: 'Planifié', badgeClass: 'bg-blue-100 text-blue-700 border-blue-200' },
  present: { label: 'En mission', badgeClass: 'bg-green-100 text-green-700 border-green-200' },
  repos: { label: 'Repos', badgeClass: 'bg-slate-100 text-slate-700 border-slate-200' },
  repos_force: { label: 'Repos forcé', badgeClass: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300' },
  repos_demande: { label: 'Repos demandé', badgeClass: 'bg-amber-100 text-amber-700 border-amber-200' },
  conge: { label: 'Congé', badgeClass: 'bg-purple-100 text-purple-700 border-purple-200' },
  cp: { label: 'Congé', badgeClass: 'bg-purple-100 text-purple-700 border-purple-200' },
  css: { label: 'Congé sans solde', badgeClass: 'bg-violet-100 text-violet-700 border-violet-200' },
  formation_binome: { label: 'Formation', badgeClass: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  formation_amzl: { label: 'Formation', badgeClass: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  absent: { label: 'Absence', badgeClass: 'bg-red-100 text-red-700 border-red-200' },
  am: { label: 'Arrêt maladie', badgeClass: 'bg-rose-100 text-rose-700 border-rose-200' },
  map: { label: 'Arrêt maladie', badgeClass: 'bg-rose-100 text-rose-700 border-rose-200' },
  en_cours_integration: { label: 'En cours d’intégration', badgeClass: 'bg-slate-200 text-slate-700 border-slate-300' },
  fdc: { label: 'Fin de contrat', badgeClass: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
  dem: { label: 'Démission', badgeClass: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
  adp: { label: 'Abandon de poste', badgeClass: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
  fpe: { label: 'Fin période d’essai', badgeClass: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
  licenciement: { label: 'Licenciement', badgeClass: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
  rupture_conventionnelle: { label: 'Rupture conventionnelle', badgeClass: 'bg-zinc-100 text-zinc-700 border-zinc-300' }
};

const IMPORTANT_NOTIFICATION_KEYWORDS = ['reclamation', 'convocation', 'formation', 'rh', 'document', 'urgent', 'exploitation'];
const MISSION_ACTIVE_STATUSES = ['prevu', 'present'];
const NO_MISSION_MODE_STATUSES = new Set([
  'repos',
  'repos_force',
  'conge',
  'cp',
  'css',
  'absent',
  'am',
  'map',
  'formation_binome',
  'formation_amzl',
  'en_cours_integration',
  'dem',
  'fdc',
  'adp',
  'fpe',
  'licenciement',
  'rupture_conventionnelle'
]);

const inferMissionType = (siteName = '') => {
  const normalized = String(siteName || '').toLowerCase();
  const isColisPrive =
    normalized.includes('colis') ||
    normalized.includes('privé') ||
    normalized.includes('prive') ||
    normalized.includes('rennes') ||
    normalized.includes('alençon') ||
    normalized.includes('alencon') ||
    normalized.includes('damigny') ||
    normalized.includes('caen');
  return isColisPrive ? 'Colis Privé' : 'Amazon';
};

const parseTimeToMinutes = (timeValue) => {
  const raw = String(timeValue || '').trim().replace('h', ':');
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour * 60 + minute;
};

const getImportantNotificationIcon = (notificationText = '') => {
  const text = String(notificationText || '').toLowerCase();
  if (text.includes('reclamation')) return MessageSquareWarning;
  if (text.includes('formation') || text.includes('convocation')) return CalendarDays;
  if (text.includes('document')) return FileText;
  if (text.includes('urgent') || text.includes('exploitation')) return Megaphone;
  return FileWarning;
};

const getStepClasses = (state) => {
  if (state === 'completed') {
    return {
      circle: 'border-emerald-200 bg-emerald-100',
      line: 'bg-emerald-400'
    };
  }
  if (state === 'active') {
    return {
      circle: 'border-amber-200 bg-amber-100',
      line: 'bg-amber-300'
    };
  }
  return {
    circle: 'border-slate-200 bg-slate-100',
    line: 'bg-slate-200'
  };
};

export default function EmployeeDashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [photoStatus, setPhotoStatus] = useState(null);
  const [importantNotifications, setImportantNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [vehicleType, setVehicleType] = useState('6m³');
  const [nextPlanning, setNextPlanning] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const todayDate = new Date();
        const todayISO = todayDate.toISOString().slice(0, 10);
        const horizonDate = new Date(todayDate);
        horizonDate.setDate(horizonDate.getDate() + 60);
        const horizonISO = horizonDate.toISOString().slice(0, 10);

        const [scheduleRes, photoRes, unreadRes, notificationsRes, vehiclesRes, assignedVehicleRes, upcomingPlanningRes] = await Promise.all([
          axios.get(`${API}/schedule/today`, { withCredentials: true }),
          axios.get(`${API}/photos/session-status`, { withCredentials: true }),
          axios.get(`${API}/notifications/unread-count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
          axios.get(`${API}/notifications`, { withCredentials: true }).catch(() => ({ data: { notifications: [] } })),
          axios.get(`${API}/vehicles`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/vehicles/user/${user?.user_id}`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/schedule/user/${user?.user_id}?start_date=${todayISO}&end_date=${horizonISO}`, { withCredentials: true }).catch(() => ({ data: [] }))
        ]);

        const scheduleData = scheduleRes.data || null;
        const photoData = photoRes.data || null;
        const notificationsData = Array.isArray(notificationsRes.data)
          ? notificationsRes.data
          : (notificationsRes.data?.notifications || []);

        setTodaySchedule(scheduleData);
        setPhotoStatus(photoData);
        setUnreadNotificationsCount(Number(unreadRes.data?.count || 0));

        const filteredImportant = notificationsData
          .filter((notif) => {
            if (notif?.status === 'processed' || notif?.status === 'archived') return false;
            const merged = `${notif.type || ''} ${notif.title || ''} ${notif.message || ''}`.toLowerCase();
            return IMPORTANT_NOTIFICATION_KEYWORDS.some((keyword) => merged.includes(keyword));
          })
          .sort((a, b) => Number(!a.read) - Number(!b.read))
          .slice(0, 3);
        setImportantNotifications(filteredImportant);

        const planningRows = Array.isArray(upcomingPlanningRes.data) ? upcomingPlanningRes.data : [];
        const nextMission = planningRows
          .filter((row) => {
            if (!row?.date) return false;
            const rowStatus = String(row.status || '').toLowerCase();
            return row.date > todayISO && MISSION_ACTIVE_STATUSES.includes(rowStatus);
          })
          .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0] || null;

        setNextPlanning(nextMission);

        const plate =
          scheduleData?.vehicle_plate ||
          photoData?.start?.vehicle_plate ||
          photoData?.end?.vehicle_plate ||
          null;
        const allVehicles = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : [];
        const matchedByPlate = plate
          ? allVehicles.find((veh) => String(veh.plate_number || '').toUpperCase() === String(plate).toUpperCase())
          : null;
        const assignedVehicle = assignedVehicleRes.data || null;
        const candidateVehicle = matchedByPlate || assignedVehicle || null;

        const resolvedType =
          candidateVehicle?.model ||
          candidateVehicle?.vehicle_type ||
          (candidateVehicle?.volume ? `${candidateVehicle.volume}m³` : null) ||
          '6m³';
        const normalized = String(resolvedType || '').toLowerCase();
        setVehicleType(normalized.includes('non défini') || normalized.includes('non defini') ? '6m³' : resolvedType);
      } catch (error) {
        console.error('Erreur chargement dashboard salarié:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.user_id]);

  const statusKey = String(todaySchedule?.status || 'repos').toLowerCase();
  const statusInfo = STATUS_CONFIG[statusKey] || {
    label: String(todaySchedule?.status || 'Non planifié'),
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const siteAssigned = todaySchedule?.site_name || user?.site_name || 'Site non renseigné';
  const rdvTime = todaySchedule?.loading_time || 'Non renseignée';
  const vehiclePlate =
    todaySchedule?.vehicle_plate ||
    photoStatus?.start?.vehicle_plate ||
    photoStatus?.end?.vehicle_plate ||
    'Non renseignée';
  const missionType = inferMissionType(siteAssigned);
  const displayVehicleType = useMemo(() => {
    const normalized = String(vehicleType || '').toLowerCase();
    if (normalized.includes('m³') || normalized.includes('m3')) return vehicleType;
    return '6m³';
  }, [vehicleType]);

  const startCompleted = Boolean(photoStatus?.start?.complete);
  const endCompleted = Boolean(photoStatus?.end?.complete);

  const hasMissionToday = useMemo(() => {
    return MISSION_ACTIVE_STATUSES.includes(statusKey) || startCompleted || endCompleted;
  }, [statusKey, startCompleted, endCompleted]);

  const isNoMissionMode = useMemo(() => {
    if (hasMissionToday) return false;
    if (NO_MISSION_MODE_STATUSES.has(statusKey)) return true;
    return true;
  }, [hasMissionToday, statusKey]);

  const isCloseMissionWindow = useMemo(() => {
    if (!todaySchedule?.end_time || !startCompleted || endCompleted) return false;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const endMinutes = parseTimeToMinutes(todaySchedule.end_time);
    if (endMinutes === null) return false;
    return nowMinutes >= (endMinutes - 60);
  }, [todaySchedule?.end_time, startCompleted, endCompleted]);

  const mainAction = useMemo(() => {
    if (!hasMissionToday) return null;
    if (!startCompleted) {
      return { label: 'Accéder au chargement', route: '/photo-upload' };
    }
    if (!endCompleted && isCloseMissionWindow) {
      return { label: 'Clôturer ma mission', route: '/photo-upload' };
    }
    return { label: 'Accéder à ma tournée', route: '/photo-upload' };
  }, [hasMissionToday, startCompleted, endCompleted, isCloseMissionWindow]);

  const missionSteps = useMemo(() => {
    if (!hasMissionToday) return [];

    const loadingState = startCompleted ? 'completed' : 'active';
    const departureState = startCompleted ? 'completed' : 'future';
    const deliveryState = endCompleted ? 'completed' : (startCompleted ? 'active' : 'future');
    const finishState = endCompleted ? 'completed' : (startCompleted && isCloseMissionWindow ? 'active' : 'future');

    return [
      {
        key: 'loading',
        index: 1,
        label: 'Chargement',
        subtitle: startCompleted ? 'Effectué' : `À effectuer ${rdvTime}`,
        state: loadingState,
        icon: Package
      },
      {
        key: 'departure',
        index: 2,
        label: 'Départ de mission',
        subtitle: startCompleted ? 'Validé' : 'À venir',
        state: departureState,
        icon: Truck
      },
      {
        key: 'delivery',
        index: 3,
        label: 'Livraison en cours',
        subtitle: endCompleted ? 'Terminée' : (startCompleted ? 'En cours' : 'À venir'),
        state: deliveryState,
        icon: Route
      },
      {
        key: 'finish',
        index: 4,
        label: 'Fin de mission',
        subtitle: endCompleted ? 'Effectuée' : 'À venir',
        state: finishState,
        icon: Flag
      }
    ];
  }, [hasMissionToday, startCompleted, endCompleted, isCloseMissionWindow, rdvTime]);

  const progressionMessage = useMemo(() => {
    if (!hasMissionToday) return '';
    if (!startCompleted) {
      return 'Une fois le chargement effectué, cliquez sur “Début de mission” pour valider votre départ.';
    }
    if (!endCompleted) {
      return 'Chargement et départ validés. Continuez la tournée puis clôturez votre mission en fin de journée.';
    }
    return 'Mission terminée. Vous pouvez consulter votre tournée et votre historique.';
  }, [hasMissionToday, startCompleted, endCompleted]);

  const todayDate = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-56" data-testid="employee-dashboard-loading-state">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#003CD7]"></div>
        </div>
      </Layout>
    );
  }

  const nextPlanningDateLabel = nextPlanning?.date
    ? new Date(`${nextPlanning.date}T00:00:00`).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
    : null;

  const nextPlanningTimeLabel = nextPlanning?.loading_time || nextPlanning?.start_time || 'Non renseignée';
  const nextPlanningSite = nextPlanning?.site_name || user?.site_name || 'Site non renseigné';

  return (
    <Layout user={user}>
      <div className="mx-auto w-full max-w-5xl space-y-3" data-testid="employee-dashboard-single-page">
        <header className="flex items-start justify-between gap-3 rounded-2xl bg-white border border-slate-200 px-4 py-3" data-testid="employee-dashboard-header">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#081A4B] leading-tight" data-testid="employee-dashboard-greeting-title">
              Bonjour, {user?.name?.split(' ')[0] || 'Livreur'} !
            </h1>
            <p className="text-base text-slate-600 capitalize mt-0.5" data-testid="employee-dashboard-date-text">{todayDate}</p>
          </div>

          <Link to="/notifications" data-testid="employee-dashboard-notifications-link">
            <Button
              variant="outline"
              className="relative h-11 w-11 p-0 rounded-xl border-slate-200 bg-white hover:bg-slate-50"
              data-testid="employee-dashboard-notifications-button"
            >
              <Bell className="w-5 h-5 text-[#003CD7]" />
              {unreadNotificationsCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 min-w-6 h-6 px-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center"
                  data-testid="employee-dashboard-notifications-badge"
                >
                  {unreadNotificationsCount}
                </span>
              )}
            </Button>
          </Link>
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="employee-mission-main-card">
          <div className="flex flex-wrap items-center justify-between gap-2" data-testid="employee-mission-main-title-row">
            <div className="inline-flex items-center gap-2 text-[#0A1737]" data-testid="employee-mission-main-title">
              <CalendarDays className="w-5 h-5 text-[#003CD7]" />
              <span className="text-2xl font-bold">Ma mission du jour</span>
            </div>

            <span
              className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold ${statusInfo.badgeClass}`}
              data-testid="employee-mission-status-badge"
            >
              {statusInfo.label}
            </span>
          </div>

          <div className="mt-2 inline-flex items-center gap-2 text-[17px] text-slate-700" data-testid="employee-mission-site-row">
            <MapPin className="w-4 h-4 text-[#003CD7]" />
            <span data-testid="employee-mission-site-value">Site assigné : {siteAssigned}</span>
          </div>

          {hasMissionToday ? (
            <>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5" data-testid="employee-mission-kpi-grid">
                <div className="rounded-xl border border-slate-200 bg-white p-3 min-h-[94px]" data-testid="employee-mission-rdv-card">
                  <p className="text-xs text-slate-500" data-testid="employee-mission-rdv-label">Heure de RDV / Chargement</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Clock3 className="w-4 h-4 text-[#003CD7]" />
                    <p className="text-4xl leading-none font-bold text-[#003CD7]" data-testid="employee-mission-rdv-value">{rdvTime}</p>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3 min-h-[94px]" data-testid="employee-mission-vehicle-card">
                  <p className="text-xs text-slate-500" data-testid="employee-mission-vehicle-label">Véhicule</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-[#003CD7]" />
                    <p className="text-lg font-semibold text-slate-900" data-testid="employee-mission-vehicle-plate">{vehiclePlate}</p>
                  </div>
                  <p className="text-sm text-slate-700 font-medium" data-testid="employee-mission-vehicle-type">{displayVehicleType}</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3 min-h-[94px]" data-testid="employee-mission-type-card">
                  <p className="text-xs text-slate-500" data-testid="employee-mission-type-label">Type de mission</p>
                  <p className="mt-2 text-3xl leading-none font-semibold text-slate-900" data-testid="employee-mission-type-value">{missionType}</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3 min-h-[94px]" data-testid="employee-mission-status-card">
                  <p className="text-xs text-slate-500" data-testid="employee-mission-status-label">Statut du jour</p>
                  <p className="mt-2 text-3xl leading-none font-semibold text-slate-900" data-testid="employee-mission-status-value">{statusInfo.label}</p>
                </div>
              </div>

              {mainAction && (
                <Link to={mainAction.route} className="block mt-3" data-testid="employee-main-action-link">
                  <Button
                    className="w-full h-12 rounded-xl bg-[#003CD7] hover:bg-[#0032b2] text-white text-2xl font-semibold tracking-tight"
                    data-testid="employee-main-action-button"
                  >
                    <Package className="w-5 h-5 mr-2" />
                    {mainAction.label}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              )}
            </>
          ) : (
            <div className="mt-3 space-y-2.5" data-testid="employee-no-mission-mode-wrapper">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="employee-no-mission-status-site-grid">
                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="employee-no-mission-status-card">
                  <p className="text-xs text-slate-500">Statut du jour</p>
                  <p className="text-4xl leading-none font-bold text-[#003CD7] mt-1" data-testid="employee-no-mission-status-value">{statusInfo.label}</p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="employee-no-mission-site-card">
                  <p className="text-xs text-slate-500">Site assigné</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#003CD7]" />
                    <p className="text-2xl leading-tight font-semibold text-slate-900" data-testid="employee-no-mission-site-value">{siteAssigned}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="employee-no-mission-info-box">
                <p className="text-3xl leading-tight font-semibold text-slate-900" data-testid="employee-no-mission-text">Aucune mission planifiée aujourd&apos;hui.</p>
                <p className="text-sm text-slate-600" data-testid="employee-no-mission-subtext">Profitez de votre journée {statusInfo.label.toLowerCase()}.</p>
              </div>
            </div>
          )}
        </section>

        {hasMissionToday && (
          <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="employee-mission-progression-card">
            <h2 className="text-2xl font-bold text-[#0A1737]" data-testid="employee-mission-progression-title">Avancement de ma mission</h2>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2.5" data-testid="employee-mission-steps-grid">
              {missionSteps.map((step, idx) => {
                const StepIcon = step.icon;
                const classes = getStepClasses(step.state);

                return (
                  <div key={step.key} className="relative rounded-xl border border-slate-200 bg-white px-3 py-2.5" data-testid={`employee-mission-step-${step.key}`}>
                    {idx < missionSteps.length - 1 && (
                      <div className={`hidden xl:block absolute top-7 -right-3 w-6 h-0.5 ${classes.line}`} />
                    )}

                    <div className="flex items-center justify-between">
                      <div className={`w-12 h-12 rounded-full border flex items-center justify-center ${classes.circle}`}>
                        {step.state === 'completed' ? (
                          <CheckCircle2 className="w-6 h-6 text-emerald-600" data-testid={`employee-step-icon-${step.key}-completed`} />
                        ) : step.state === 'active' ? (
                          <AlertCircle className="w-6 h-6 text-amber-500" data-testid={`employee-step-icon-${step.key}-active`} />
                        ) : (
                          <Circle className="w-6 h-6 text-slate-300" data-testid={`employee-step-icon-${step.key}-future`} />
                        )}
                      </div>
                      <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold flex items-center justify-center" data-testid={`employee-step-index-${step.key}`}>
                        {step.index}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-1.5">
                      <StepIcon className="w-4 h-4 text-[#003CD7]" />
                      <p className="text-2xl font-semibold text-slate-900 leading-tight" data-testid={`employee-step-label-${step.key}`}>{step.label}</p>
                    </div>

                    <p className="text-sm text-slate-600" data-testid={`employee-step-hint-${step.key}`}>{step.subtitle}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2" data-testid="employee-mission-progression-message-box">
              <p className="text-sm text-amber-800" data-testid="employee-mission-progression-message">{progressionMessage}</p>
            </div>
          </section>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="employee-important-notifications-card">
          <div className="flex items-center justify-between" data-testid="employee-important-notifications-header">
            <h2 className="text-2xl font-bold text-[#0A1737]" data-testid="employee-important-notifications-title">Notifications importantes</h2>
            <Link to="/notifications" className="text-[#003CD7] text-sm font-semibold hover:underline" data-testid="employee-important-notifications-see-all-link">
              Voir toutes
            </Link>
          </div>

          <div className="mt-2 rounded-xl border border-slate-200 overflow-hidden" data-testid="employee-important-notifications-list">
            {importantNotifications.length === 0 ? (
              <p className="text-sm text-slate-500 px-3 py-2.5" data-testid="employee-important-notifications-empty">
                Aucune notification importante.
              </p>
            ) : (
              importantNotifications.map((notif) => {
                const mergedText = `${notif.type || ''} ${notif.title || ''} ${notif.message || ''}`;
                const NotifIcon = getImportantNotificationIcon(mergedText);
                return (
                  <Link
                    key={notif.notification_id}
                    to="/notifications"
                    className="block border-b border-slate-100 last:border-b-0"
                    data-testid={`employee-important-notification-link-${notif.notification_id}`}
                  >
                    <div
                      className={`px-3 py-2.5 flex items-center gap-3 ${notif.read ? 'bg-white' : 'bg-blue-50'}`}
                      data-testid={`employee-important-notification-item-${notif.notification_id}`}
                    >
                      <NotifIcon className="w-5 h-5 text-[#003CD7] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-semibold text-slate-900" data-testid={`employee-important-notification-title-${notif.notification_id}`}>
                          {notif.title}
                        </p>
                        <p className="text-sm text-slate-600 truncate" data-testid={`employee-important-notification-message-${notif.notification_id}`}>
                          {notif.message}
                        </p>
                      </div>
                      {!notif.read && (
                        <span className="w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center" data-testid={`employee-important-notification-unread-badge-${notif.notification_id}`}>
                          1
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </section>

        {isNoMissionMode && (
          <section className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="employee-next-planning-card">
            <div className="flex items-center gap-2" data-testid="employee-next-planning-header">
              <CalendarDays className="w-5 h-5 text-emerald-600" />
              <h2 className="text-2xl font-bold text-[#0A1737]" data-testid="employee-next-planning-title">Mon prochain planning</h2>
            </div>

            {nextPlanning ? (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="employee-next-planning-content">
                <p className="text-xs text-slate-500" data-testid="employee-next-planning-label">Prochaine mission</p>
                <p className="text-2xl leading-tight font-semibold text-slate-900 mt-1 capitalize" data-testid="employee-next-planning-date">{nextPlanningDateLabel}</p>

                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-slate-700" data-testid="employee-next-planning-meta">
                  <div className="inline-flex items-center gap-1.5" data-testid="employee-next-planning-time-wrap">
                    <Clock3 className="w-4 h-4 text-emerald-700" />
                    <span data-testid="employee-next-planning-time">{nextPlanningTimeLabel}</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5" data-testid="employee-next-planning-site-wrap">
                    <MapPin className="w-4 h-4 text-emerald-700" />
                    <span data-testid="employee-next-planning-site">{nextPlanningSite}</span>
                  </div>
                </div>

                <Link to="/planning" className="block mt-3" data-testid="employee-next-planning-button-link">
                  <Button
                    variant="outline"
                    className="w-full h-11 rounded-xl border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                    data-testid="employee-next-planning-button"
                  >
                    Voir mon planning
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid="employee-next-planning-empty">
                <p className="text-sm text-slate-600">Aucune mission à venir planifiée pour le moment.</p>
                <Link to="/planning" className="inline-flex items-center text-sm text-[#003CD7] font-semibold mt-2" data-testid="employee-next-planning-empty-link">
                  Voir mon planning
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            )}
          </section>
        )}
      </div>
    </Layout>
  );
}
